import styled from 'styled-components';
import { tokens } from 'design-tokens/warwick/tokens';
import { flexColumn, flexCenter, flexCenterVertical } from 'design-tokens/warwick/mixins';
import { ContentBody } from 'design-tokens/warwick/common';

const { primitive } = tokens;

// ─── ContentBody extension ────────────────────────────────────────────────────
// Extends ContentBody without modifying it: shrinks to content height
// instead of stretching to fill remaining flex space.
export const InfoContentBody = styled(ContentBody)`
  flex: 0 0 auto;
`;

// ─── Outer wrapper ────────────────────────────────────────────────────────────

export const InfoBody = styled.div`
  ${flexColumn}
  padding: ${primitive.space[24]};
  > * + * {
    margin-top: ${primitive.space[52]};
  }
`;

// ─── Section 1: Hotel overview card ──────────────────────────────────────────

export const HotelCard = styled.div`
  ${flexColumn}
`;

export const HotelCardHeader = styled.div`
  ${flexColumn}
  > * + * {
    margin-top: ${primitive.space[6]} !important;
  }
`;

export const HotelName = styled.h2`
  font-family: ${primitive.typography.fontFamily.base};
  font-size: ${primitive.typography.fontSize[26]};
  font-weight: ${primitive.typography.fontWeight.semiBold};
  color: ${tokens.semantic.color.text.onLight};
  line-height: ${primitive.typography.lineHeight.tight};
  margin: 0;
`;

export const HotelAddress = styled.p`
  ${flexCenterVertical}
  > * + * {
    margin-inline-start: ${primitive.space[5]};
  }
  font-family: ${primitive.typography.fontFamily.base};
  font-size: ${primitive.typography.fontSize[19]};
  font-weight: ${primitive.typography.fontWeight.regular};
  color: ${tokens.semantic.color.text.onLight};
  line-height: ${primitive.typography.lineHeight.tight};
  margin: 0;
`;

// Horizontal gold separator rendered after the address
export const HotelCardDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${primitive.color.gold};
  margin: ${primitive.space[15]} 0;
`;

// ─── 3-column content row ─────────────────────────────────────────────────────

export const HotelContentRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  > * + * {
    margin-inline-start: ${primitive.space[24]};
  }
`;

// ─── Manager card (left, ~28%) ────────────────────────────────────────────────

export const ManagerCard = styled.div`
  ${flexColumn}
  width: 28%;
  flex-shrink: 0;
  background-color: ${primitive.color.purple};
  border-radius: ${primitive.radius[12]};
  padding: ${primitive.space[24]};
  box-shadow: ${primitive.shadow.subtle};
  justify-content: center;
  > * + * {
    margin-top: ${primitive.space[44]} !important;
  }
`;

// Row 1: avatar + name/title side by side
export const ManagerTopRow = styled.div`
  ${flexCenterVertical}
  > * + * {
    margin-inline-start: ${primitive.space[16]};
  }
`;

export const ManagerAvatar = styled.div`
  width: 107px;
  height: 107px;
  border-radius: ${primitive.radius[18]};
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover !important;
  }
`;

export const ManagerNameGroup = styled.div`
  ${flexColumn}
  align-items: flex-start;
  > * + * {
    margin-top: ${primitive.space[4]} !important;
  }
`;

export const ManagerName = styled.p`
  font-family: ${primitive.typography.fontFamily.base};
  font-size: ${primitive.typography.fontSize[20]};
  font-weight: ${primitive.typography.fontWeight.semiBold};
  color: ${primitive.color.white};
  line-height: ${primitive.typography.lineHeight.tight};
  margin: 0;
`;

export const ManagerTitle = styled.p`
  font-family: ${primitive.typography.fontFamily.base};
  font-size: ${primitive.typography.fontSize[14]};
  font-weight: ${primitive.typography.fontWeight.regular};
  color: ${primitive.color.gold};
  line-height: ${primitive.typography.lineHeight.tight};
  margin: 0;
`;

// Row 2: quote / message
export const ManagerMessage = styled.p`
  font-family: ${primitive.typography.fontFamily.base};
  font-size: ${primitive.typography.fontSize[14]};
  font-weight: ${primitive.typography.fontWeight.regular};
  color: ${primitive.color.white};
  line-height: ${primitive.typography.lineHeight.relaxed};
  margin: 0;
  text-align: left;
`;

// ─── About Hotel section (centre, flex-1) ────────────────────────────────────

export const AboutSection = styled.div`
  ${flexColumn}
  flex: 1;
  background-color: ${primitive.color.whiteAlpha70};
  border-radius: ${primitive.radius[12]};
  padding: ${primitive.space[24]};
  box-shadow: ${primitive.shadow.subtle};
  justify-content: space-between;
`;

export const AboutHeading = styled.p`
  font-family: ${primitive.typography.fontFamily.base};
  font-size: ${primitive.typography.fontSize[17]};
  font-weight: ${primitive.typography.fontWeight.semiBold};
  color: ${primitive.color.gold};
  line-height: ${primitive.typography.lineHeight.tight};
  margin: 0;
`;

export const AboutParagraphGroup = styled.div`
  ${flexColumn}
  > * + * {
    margin-top: ${primitive.space[12]} !important;
  }
`;

export const AboutParagraph = styled.p`
  font-family: ${primitive.typography.fontFamily.base};
  font-size: ${primitive.typography.fontSize[17]};
  font-weight: ${primitive.typography.fontWeight.regular};
  color: ${tokens.semantic.color.text.onLight};
  line-height: ${primitive.typography.lineHeight.relaxed};
  margin: 0;
  text-align: justify;
`;

// ─── Stats column (right, ~15%) ───────────────────────────────────────────────

export const StatsColumn = styled.div`
  ${flexColumn}
  width: 15%;
  flex-shrink: 0;
  > * + * {
    margin-top: ${primitive.space[12]} !important;
  }
`;

export const StatChip = styled.div`
  ${flexCenterVertical}
  flex: 1;
  background-color: ${primitive.color.whiteAlpha70};
  border-radius: ${primitive.radius[12]};
  padding: ${primitive.space[15]};
  box-shadow: ${primitive.shadow.subtle};
  /* Child spacing — explicit LTR/RTL fallback for Tizen compatibility */
  > * + * {
    margin-left: ${primitive.space[15]};
  }

  [dir="rtl"] & > * + * {
    margin-left: 0;
    margin-right: ${primitive.space[15]};
  }
`;

export const StatIconBox = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const StatTextGroup = styled.div`
  ${flexColumn}
  > * + * {
    margin-top: ${primitive.space[4]} !important;
  }
`;

export const StatValue = styled.p`
  font-family: ${primitive.typography.fontFamily.base};
  font-size: ${primitive.typography.fontSize[23]};
  font-weight: ${primitive.typography.fontWeight.semiBold};
  color: ${tokens.semantic.color.text.onLight};
  line-height: ${primitive.typography.lineHeight.tight};
  margin: 0;
`;

export const StatLabel = styled.p`
  font-family: ${primitive.typography.fontFamily.base};
  font-size: ${primitive.typography.fontSize[14]};
  font-weight: ${primitive.typography.fontWeight.regular};
  color: ${tokens.semantic.color.text.onLight};
  line-height: ${primitive.typography.lineHeight.tight};
  margin: 0;
`;

// ─── Section 2: Premium Services & Amenities ─────────────────────────────────

export const AmenitiesSection = styled.div`
  ${flexColumn}
  > * + * {
    margin-top: ${primitive.space[16]};
  }
`;

export const AmenitiesSectionHeading = styled.h3`
  font-family: ${primitive.typography.fontFamily.base};
  font-size: ${primitive.typography.fontSize[26]};
  font-weight: ${primitive.typography.fontWeight.semiBold};
  color: ${tokens.semantic.color.text.onLight};
  line-height: ${primitive.typography.lineHeight.tight};
  margin: 0;
`;

export const AmenitiesRow = styled.div`
  display: flex;
  flex-direction: row;
  > * + * {
    margin-inline-start: ${primitive.space[24]};
  }
`;

export const AmenityCard = styled.div`
  ${flexColumn}
  flex: 1;
  align-items: center;
  text-align: center;
  background-color: ${primitive.color.purple};
  border-radius: ${primitive.radius[12]};
  padding: ${primitive.space[20]} ${primitive.space[40]};
  > * + * {
    margin-top: ${primitive.space[8]} !important;
  }
  box-shadow: ${primitive.shadow.amenityCard};
`;

// Gold icon container with rounded corners, 60×60
export const AmenityIconBox = styled.div`
  ${flexCenter}
  width: 60px;
  height: 60px;
  background-color: ${primitive.color.gold};
  border-radius: ${primitive.radius[12]};
  padding: ${primitive.space[11]};
  flex-shrink: 0;
  box-sizing: border-box;
  margin-bottom: ${primitive.space[8]};
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const AmenityName = styled.p`
  font-family: ${primitive.typography.fontFamily.base};
  font-size: ${primitive.typography.fontSize[17]};
  font-weight: ${primitive.typography.fontWeight.semiBold};
  color: ${primitive.color.white};
  line-height: ${primitive.typography.lineHeight.tight};
  margin: 0;
`;

export const AmenityDescription = styled.p`
  font-family: ${primitive.typography.fontFamily.base};
  font-size: ${primitive.typography.fontSize[14]};
  font-weight: ${primitive.typography.fontWeight.regular};
  color: ${primitive.color.gold};
  line-height: ${primitive.typography.lineHeight.tight};
  margin: 0;
`;
